import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { clearToken } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import HelpRequestsPanel from "../components/HelpRequestsPanel";
import CompletionForecastCard from "../components/CompletionForecastCard";
import ConfirmDialog from "../components/ConfirmDialog";
import { Plus, Mail, Copy, ExternalLink, Loader2, Building2, Calendar, RefreshCw, Bell, BarChart3, Pencil, Send, Download, BookOpen, Trash2, Eye, Users } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-600 border border-slate-200" },
  in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  submitted:   { label: "Submitted",   cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ company_name: "", client_email: "", client_name: "", link_expires_in_days: 30 });
  const [createdLink, setCreatedLink] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);  // {id, name} or null
  const [stats, setStats] = useState(null);
  const [openHelp, setOpenHelp] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const selectableItems = () => items.filter(o => o.status !== "submitted");
  const toggleSelectAll = () => {
    const all = selectableItems();
    setSelected(prev => prev.size === all.length ? new Set() : new Set(all.map(o => o.id)));
  };
  const bulkRemind = async () => {
    if (selected.size === 0) return;
    setBulkSending(true);
    try {
      const { data } = await api.post(`/onboardings/bulk-remind`, { ids: Array.from(selected) });
      toast.success(`Reminders sent: ${data.sent}${data.skipped?.length ? ` · skipped: ${data.skipped.length}` : ""}`);
      setSelected(new Set());
      load();
    } catch { toast.error("Bulk remind failed"); }
    finally { setBulkSending(false); }
  };

  const load = async () => {
    try {
      setLoading(true);
      const [a, b, c] = await Promise.all([
        api.get("/onboardings"),
        api.get("/reports/overview"),
        api.get("/admin/help-requests"),
      ]);
      setItems(a.data.onboardings || []);
      setStats(b.data);
      setOpenHelp(c.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) { clearToken(); nav("/login"); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resolveHelp = async (onb_id, req_id, payload) => {
    try {
      const { data } = await api.post(`/onboardings/${onb_id}/help-requests/${req_id}/resolve`, payload);
      const bits = [];
      if (payload.send_email_to_client) bits.push("client emailed");
      if (data.saved_tip_id) bits.push("saved as tip");
      toast.success(bits.length ? `Resolved · ${bits.join(" · ")}` : "Resolved");
      await load();
    } catch { toast.error("Failed to resolve"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      // Coerce expiry: 0/empty → no expiry; otherwise integer days
      if (form.link_expires_in_days === "" || form.link_expires_in_days === null || form.link_expires_in_days === undefined) {
        payload.link_expires_in_days = 30;
      } else {
        payload.link_expires_in_days = Number(form.link_expires_in_days);
      }
      const { data } = await api.post("/onboardings", payload);
      setCreatedLink(data.client_link);
      setForm({ company_name: "", client_email: "", client_name: "", link_expires_in_days: 30 });
      await load();
      toast.success(`Onboarding created. Link emailed${payload.link_expires_in_days ? ` (expires in ${payload.link_expires_in_days} days)` : " (no expiry)"}.`);
    } catch { toast.error("Failed to create onboarding"); }
    finally { setCreating(false); }
  };

  const deleteOnboarding = async (id, name) => {
    // ConfirmDialog opens via state; the actual delete runs from onConfirm below
    setConfirmDelete({ id, name });
  };

  const doDeleteOnboarding = async () => {
    if (!confirmDelete) return;
    const { id, name } = confirmDelete;
    try {
      await api.delete(`/onboardings/${id}`);
      toast.success(`Deleted ${name}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const copyLink = (l) => { navigator.clipboard.writeText(l); toast.success("Link copied"); };
  const resend = async (id) => {
    // Rotate: invalidates previous link + resets expiry to default 30 days. Hold
    // Shift while clicking to be prompted for a custom number of days.
    const customDays = window.event?.shiftKey
      ? Number(window.prompt("New link expiry in days (0 = no expiry):", "30"))
      : 30;
    try {
      const { data } = await api.post(`/onboardings/${id}/resend`, { extend_days: Number.isFinite(customDays) ? customDays : 30 });
      toast.success(data.link_expires_at ? `Link rotated · expires ${new Date(data.link_expires_at).toLocaleDateString()}` : "Link rotated · no expiry");
      copyLink(data.client_link);
      load();
    } catch { toast.error("Failed"); }
  };
  const remind = async (id) => {
    try { await api.post(`/onboardings/${id}/remind`); toast.success("Reminder sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendDigest = async () => {
    try { await api.post(`/reports/digest/send-now`); toast.success("Digest email sent to admins"); }
    catch { toast.error("Failed"); }
  };
  const sendStuckDigest = async () => {
    try {
      const { data } = await api.post(`/reports/stuck-digest/send-now`);
      if (data.count === 0) toast.info("No stuck onboardings to report");
      else toast.success(`Stuck digest sent (${data.count} client${data.count === 1 ? "" : "s"})`);
    } catch { toast.error("Failed"); }
  };
  const downloadAudit = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/audit.csv`, { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `sentry-audit-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit log downloaded");
    } catch { toast.error("Download failed"); }
  };
  const downloadGuide = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/guide.pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "Sentry-Cloud-IT-Team-Guide.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Team guide downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <BrandHeader showSettings showAuditLog showSecurityEvents showImport showClients showLogout>
        <Button onClick={() => { setOpen(true); setCreatedLink(null); }} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="new-onboarding-btn">
          <Plus size={16} className="mr-2"/>New onboarding
        </Button>
      </BrandHeader>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-brand-primary">Onboardings</h1>
            <p className="text-slate-600 mt-1">Send new clients a secure link. Track progress. Sync to ITGlue when ready.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/admin/clients")} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="clients-btn">
              <Users size={14} className="mr-1.5"/>Clients
            </Button>
            <Button variant="outline" onClick={downloadGuide} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="team-guide-btn">
              <BookOpen size={14} className="mr-1.5"/>Team guide
            </Button>
            <Button variant="outline" onClick={downloadAudit} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="audit-csv-btn">
              <Download size={14} className="mr-1.5"/>Audit log
            </Button>
            <Button variant="outline" onClick={sendDigest} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="send-digest-btn">
              <Send size={14} className="mr-1.5"/>Send digest now
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="stats-row">
            <Card className="bg-white border-slate-200 p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500">Total</div>
              <div className="text-2xl font-bold text-brand-primary mt-1">{stats.totals.all}</div>
            </Card>
            <Card className="bg-white border-slate-200 p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500">In progress</div>
              <div className="text-2xl font-bold text-amber-600 mt-1">{stats.totals.in_progress || 0}</div>
            </Card>
            <Card className="bg-white border-slate-200 p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500">Submitted</div>
              <div className="text-2xl font-bold text-emerald-600 mt-1">{stats.totals.submitted || 0}</div>
            </Card>
            <Card className="bg-white border-slate-200 p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500">Avg days to complete</div>
              <div className="text-2xl font-bold text-brand-primary mt-1">{stats.avg_days_to_complete ?? "—"}</div>
            </Card>
          </div>
        )}

        {openHelp.length > 0 && (
          <HelpRequestsPanel
            items={openHelp}
            onResolve={resolveHelp}
          />
        )}

        {stats?.completion_forecast?.length > 0 && (
          <CompletionForecastCard forecast={stats.completion_forecast} />
        )}

        {stats?.stale_onboardings?.length > 0 && (
          <Card className="bg-amber-50 border-amber-200 p-5 mb-6 shadow-sm" data-testid="stale-panel">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2"><BarChart3 size={16} className="text-amber-600"/><div className="font-medium text-amber-800">Stale onboardings ({stats.stale_onboardings.length})</div></div>
              <Button onClick={sendStuckDigest} size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="send-stuck-digest-btn">
                <Send size={12} className="mr-1.5"/>Email stuck-client digest
              </Button>
            </div>
            <p className="text-xs text-amber-700/80 mb-3">No activity for 3+ days — consider sending a reminder.</p>
            <div className="space-y-2">
              {stats.stale_onboardings.slice(0,5).map(s => (
                <div key={s.id} className="flex items-center justify-between bg-white border border-amber-200 rounded p-2.5 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium text-slate-900">{s.company_name}</span>
                    <span className="text-slate-500"> · {s.client_email} · {s.days_stale}d · {s.progress}%</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => remind(s.id)} className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid={`stale-remind-${s.id}`}>
                    <Bell size={12} className="mr-1"/>Remind
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {stats?.section_stats?.length > 0 && (() => {
          const ss = stats.section_stats;
          const maxStuck = Math.max(1, ...ss.map(s => s.stuck_count));
          const sectionsWithTime = ss.filter(s => s.median_minutes !== null).sort((a, b) => (b.median_minutes || 0) - (a.median_minutes || 0));
          const sectionsByStuck = [...ss].filter(s => s.stuck_count > 0).sort((a, b) => b.stuck_count - a.stuck_count);
          return (
            <Card className="bg-white border-slate-200 p-5 mb-6 shadow-sm" data-testid="section-analytics-panel">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 size={16} className="text-brand-primary"/>
                <div className="font-medium text-brand-primary">Section analytics</div>
              </div>
              <p className="text-xs text-slate-500 mb-4">Where clients spend the most time and where they get stuck. Helps you spot drop-off and tune the wizard.</p>

              <div className="grid md:grid-cols-2 gap-5">
                {/* Drop-off — sections people are currently stuck on */}
                <div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Where clients are stuck</div>
                  {sectionsByStuck.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No in-progress clients right now.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {sectionsByStuck.slice(0, 6).map(s => (
                        <div key={s.id} className="text-xs" data-testid={`stuck-row-${s.id}`}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-slate-700 truncate pr-2" title={s.title}>{s.title}</span>
                            <span className="font-semibold text-slate-900">{s.stuck_count}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(s.stuck_count / maxStuck) * 100}%` }}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Time-per-section (median) */}
                <div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Median time per section</div>
                  {sectionsWithTime.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">Not enough data yet — clients need to save through a couple of sections first.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {sectionsWithTime.slice(0, 6).map(s => {
                        const max = Math.max(...sectionsWithTime.map(x => x.median_minutes || 0));
                        return (
                          <div key={s.id} className="text-xs" data-testid={`time-row-${s.id}`}>
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-slate-700 truncate pr-2" title={s.title}>{s.title}</span>
                              <span className="font-semibold text-slate-900">{s.median_minutes} min<span className="text-slate-400 font-normal ml-1">(n={s.sample_size})</span></span>
                            </div>
                            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${((s.median_minutes || 0) / max) * 100}%`, background: "#0F1644" }}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Per-section fill rate strip */}
              <div className="mt-5 pt-4 border-t border-slate-200">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Section fill rate (% of submitted)</div>
                <div className="grid grid-cols-4 sm:grid-cols-8 md:grid-cols-16 gap-1">
                  {ss.map(s => {
                    const fp = s.filled_pct_of_submitted ?? s.filled_pct ?? 0;
                    return (
                      <div key={s.id} className="group relative" title={`${s.title} — ${fp}% filled`}>
                        <div className="h-12 rounded-sm bg-slate-100 overflow-hidden flex items-end">
                          <div className="w-full transition-all duration-500" style={{ height: `${fp}%`, background: fp >= 70 ? "#10b981" : fp >= 30 ? "#f59e0b" : "#ef4444" }} />
                        </div>
                        <div className="text-[10px] text-slate-500 text-center mt-1 truncate">{fp}%</div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-2">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/>≥70%</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/>30-69%</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500"/>&lt;30%</span>
                </div>
              </div>
            </Card>
          );
        })()}

        {items.length > 0 && (
          <div className="flex items-center justify-between mb-3 px-1 flex-wrap gap-2" data-testid="bulk-bar">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={selectableItems().length > 0 && selected.size === selectableItems().length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox"/>
              <span>Select all non-submitted ({selectableItems().length})</span>
            </label>
            {selected.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">{selected.size} selected</span>
                <Button size="sm" onClick={bulkRemind} disabled={bulkSending} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="bulk-remind-btn">
                  {bulkSending ? <Loader2 className="animate-spin" size={14}/> : <><Bell size={14} className="mr-1.5"/>Send {selected.size} reminder{selected.size>1?"s":""}</>}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-slate-600" data-testid="clear-selection-btn">Clear</Button>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16}/>Loading…</div>
        ) : items.length === 0 ? (
          <Card className="bg-white border-slate-200 p-12 text-center shadow-sm" data-testid="empty-state">
            <Building2 className="mx-auto text-slate-400" size={40}/>
            <h3 className="mt-4 text-lg font-medium text-slate-900">No onboardings yet</h3>
            <p className="text-slate-500 mt-1">Create your first one to send a secure link to a new client.</p>
            <Button onClick={() => setOpen(true)} className="mt-6 font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="empty-create-btn">
              <Plus size={16} className="mr-2"/>Create onboarding
            </Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="onboardings-list">
            {items.map((o) => {
              const s = STATUS[o.status] || STATUS.not_started;
              const canSelect = o.status !== "submitted";
              const isSelected = selected.has(o.id);
              return (
                <Card key={o.id} className={`bg-white border-slate-200 p-5 hover:shadow transition shadow-sm ${isSelected ? "border-[#00C7FF] ring-1 ring-[#00C7FF]/30" : "hover:border-[#00C7FF]"}`} data-testid={`onb-row-${o.id}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      {canSelect ? (
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(o.id)} className="shrink-0" data-testid={`select-${o.id}`}/>
                      ) : <div className="w-4 shrink-0"/>}
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-semibold text-lg text-brand-primary">{o.company_name}</div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                          {o.next_reminder_at && o.status !== "submitted" && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" title="Scheduled reminder">
                              <Calendar size={10} className="inline mr-1"/>Reminder {new Date(o.next_reminder_at).toLocaleDateString()}
                            </span>
                          )}
                          {/* Link-expiry badge: warns when close, blocks when past */}
                          {(() => {
                            if (!o.link_expires_at || o.status === "submitted") return null;
                            const exp = new Date(o.link_expires_at);
                            const days = Math.ceil((exp - new Date()) / 86400000);
                            const expired = days < 0;
                            const soon = !expired && days <= 7;
                            const cls = expired ? "bg-rose-50 text-rose-700 border-rose-200" : soon ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200";
                            const label = expired ? `Link expired ${-days}d ago` : soon ? `Link expires in ${days}d` : `Link valid until ${exp.toLocaleDateString()}`;
                            return (
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${cls}`} title="Click Resend to rotate & extend" data-testid={`link-expiry-${o.id}`}>
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                          <span className="inline-flex items-center gap-1.5"><Mail size={12}/>{o.client_email}</span>
                          <span className="inline-flex items-center gap-1.5"><Calendar size={12}/>Created {new Date(o.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => remind(o.id)} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid={`remind-${o.id}`} disabled={o.status==="submitted"}>
                        <Bell size={14} className="mr-1.5"/>Remind
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => resend(o.id)} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid={`resend-${o.id}`}>
                        <RefreshCw size={14} className="mr-1.5"/>Resend
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => nav(`/admin/onboarding/${o.id}/preview`)} className="text-violet-700 hover:bg-violet-50" data-testid={`preview-${o.id}`} title="Read-only preview — see exactly what the client sees, no edits saved.">
                        <Eye size={14} className="mr-1.5"/>Preview
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => nav(`/admin/onboarding/${o.id}/edit`)} className="border-brand-accent/40 text-brand-primary hover:bg-brand-accent/10" data-testid={`edit-${o.id}`} title="Open the onboarding wizard as if you were the client — useful for kicking off, helping over a call, or completing on their behalf.">
                        <Pencil size={14} className="mr-1.5"/>Fill as client
                      </Button>
                      <Button size="sm" onClick={() => nav(`/admin/onboarding/${o.id}`)} className="font-semibold" style={{ background: "#0F1644", color: "#fff" }} data-testid={`view-${o.id}`}>
                        View <ExternalLink size={14} className="ml-1.5"/>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteOnboarding(o.id, o.company_name)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-${o.id}`} title="Delete onboarding (remote ITGlue records untouched)">
                        <Trash2 size={14}/>
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); }}>
        <DialogContent className="bg-white text-slate-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-brand-primary">New onboarding</DialogTitle>
            <DialogDescription>Enter company details — we&apos;ll email a secure onboarding link to the client.</DialogDescription>
          </DialogHeader>
          {!createdLink ? (
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Company name</Label>
                <Input value={form.company_name} onChange={(e)=>setForm({...form, company_name: e.target.value})} required data-testid="form-company-input"/>
              </div>
              <div className="space-y-2">
                <Label>Client contact name</Label>
                <Input value={form.client_name} onChange={(e)=>setForm({...form, client_name: e.target.value})} placeholder="(optional)" data-testid="form-name-input"/>
              </div>
              <div className="space-y-2">
                <Label>Client email</Label>
                <Input type="email" value={form.client_email} onChange={(e)=>setForm({...form, client_email: e.target.value})} required data-testid="form-email-input"/>
                <p className="text-xs text-slate-500">They&apos;ll get a secure one-click link to begin.</p>
              </div>
              <div className="space-y-2">
                <Label>Link expires after</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="365"
                    value={form.link_expires_in_days}
                    onChange={(e)=>setForm({...form, link_expires_in_days: e.target.value})}
                    data-testid="form-expires-input"
                    className="w-24"
                  />
                  <span className="text-sm text-slate-600">days</span>
                </div>
                <p className="text-xs text-slate-500">Set to <strong>0</strong> for no expiry. After submission the client always retains access to their kickoff packet.</p>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={creating} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="create-submit-btn">
                  {creating ? <Loader2 className="animate-spin" size={16}/> : "Create & send link"}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4" data-testid="created-link-panel">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 text-sm">
                Onboarding created. Email sent. You can also copy the link below.
              </div>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 flex items-center gap-2">
                <code className="flex-1 truncate text-xs text-slate-700">{createdLink}</code>
                <Button size="sm" variant="outline" onClick={()=>copyLink(createdLink)} className="border-slate-300" data-testid="copy-link-btn"><Copy size={14}/></Button>
              </div>
              <DialogFooter>
                <Button onClick={()=>setOpen(false)} className="font-semibold" style={{ background: "#0F1644", color: "#fff" }}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title={confirmDelete ? `Delete "${confirmDelete.name}"?` : ""}
        description={"This removes the onboarding record + its files, help-requests, and activity log from Sentry.\n\nRemote artifacts on your ITGlue tenant are NOT touched — clean those up manually if needed."}
        confirmLabel="Delete onboarding"
        cancelLabel="Keep it"
        destructive
        testId="delete-onboarding-confirm"
        onConfirm={doDeleteOnboarding}
      />

      <BrandFooter />
    </div>
  );
}
